/**
 * CalendarFeedPage - Personal iCal Feed Settings
 *
 * Allows users to get their personal calendar feed URL
 * for subscribing in Google Calendar, Apple Calendar, or Outlook.
 *
 * Issue #941: Club iCal calendar feed (subscribable)
 * Issue #943: Feed mode filter (all vs RSVP only)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOroMutation, OroOperation } from '../../hooks/useOroMutation';
import { TopBar, Button, Skeleton, ConfirmDialog, OroLoader } from '../../components';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
  getCalendarFeed,
  regenerateCalendarFeed,
  updateCalendarFeedMode,
  type CalendarFeedResponse,
  type CalendarFeedMode,
  type UpdateFeedModeResponse,
} from '../../lib/api/calendar';
import { Calendar, RefreshCw } from 'lucide-react';
import CalendarSubscribeActions from '../../components/CalendarSubscribeActions';

export default function CalendarFeedPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  useDocumentTitle('Calendar Feed');

  // Fetch calendar feed URL
  const { data, isLoading, error } = useQuery({
    queryKey: ['calendar-feed'],
    queryFn: getCalendarFeed,
  });

  // Regenerate mutation
  const regenerateMutation = useOroMutation<CalendarFeedResponse, void>({
    mutationFn: regenerateCalendarFeed,
    oro: { op: OroOperation.RegenerateCalendarFeed },
    successToast: 'Calendar feed URL regenerated',
    onSuccess: (newData) => {
      queryClient.setQueryData(['calendar-feed'], newData);
    },
  });

  // Feed mode mutation (#943)
  const feedModeMutation = useOroMutation<UpdateFeedModeResponse, CalendarFeedMode>({
    mutationFn: updateCalendarFeedMode,
    oro: { op: OroOperation.UpdateCalendarFeedMode },
    successToast: 'Feed preference updated',
    onSuccess: (result) => {
      // Update cached data with new feed_mode
      queryClient.setQueryData(['calendar-feed'], (old: CalendarFeedResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          calendar_feed: {
            ...old.calendar_feed,
            feed_mode: result.feed_mode,
          },
        };
      });
    },
  });

  const currentFeedMode = data?.calendar_feed?.feed_mode || 'all';

  const handleRegenerateClick = () => {
    setShowRegenerateConfirm(true);
  };

  const handleRegenerateConfirm = () => {
    setShowRegenerateConfirm(false);
    regenerateMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Calendar Feed" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14 p-4 space-y-6">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Calendar Feed" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14 p-4">
          <div className="bg-oro-error/10 border border-oro-error/20 rounded-xl p-4">
            <p className="text-oro-error">Failed to load calendar feed. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  const feedUrl = data?.calendar_feed?.url || '';

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Calendar Feed" onBack={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto pt-14 p-4 space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto">
            <Calendar className="w-6 h-6 text-accent" />
          </div>
          <h1 className="text-xl font-semibold text-text-primary">Personal Calendar Feed</h1>
          <p className="text-text-secondary text-sm">
            Subscribe to see club events in your favorite calendar app
          </p>
        </div>

        {/* Feed Mode Toggle (#943) */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-3">
          <div>
            <h2 className="font-medium text-text-primary">Which events to include?</h2>
            <p className="text-sm text-text-muted mt-1">
              Choose what appears in your synced calendar
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => feedModeMutation.mutate('all')}
              disabled={feedModeMutation.isPending}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                currentFeedMode === 'all'
                  ? 'bg-accent text-black'
                  : 'bg-oro-surface-alt border border-oro-border text-text-secondary hover:border-oro-border-hover'
              }`}
            >
              All club events
            </button>
            <button
              onClick={() => feedModeMutation.mutate('rsvp_only')}
              disabled={feedModeMutation.isPending}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                currentFeedMode === 'rsvp_only'
                  ? 'bg-accent text-black'
                  : 'bg-oro-surface-alt border border-oro-border text-text-secondary hover:border-oro-border-hover'
              }`}
            >
              Events I'm attending
            </button>
          </div>

          <p className="text-xs text-text-muted">
            {currentFeedMode === 'all'
              ? 'Your calendar will show every event from your clubs'
              : 'Your calendar will only show events you\'ve RSVP\'d to'}
          </p>
        </div>

        {/* Subscribe Actions + Regenerate */}
        <CalendarSubscribeActions
          feedUrl={feedUrl}
          calendarName="ORO move\u2122 Calendar"
        />

        {/* Regenerate Token */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-text-primary">Regenerate URL</h3>
              <p className="text-xs text-text-muted mt-0.5">
                Invalidates the current URL and creates a new one
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRegenerateClick}
              disabled={regenerateMutation.isPending}
              className="flex items-center justify-center gap-2"
            >
              {regenerateMutation.isPending ? (
                <OroLoader variant="bounce" size={16} label="Regenerating" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Security Note */}
        <div className="bg-oro-surface-alt border border-oro-border rounded-xl p-4">
          <p className="text-text-muted text-sm">
            <strong className="text-text-secondary">Security note:</strong> This URL is private to you.
            Anyone with this URL can see your club events. If you accidentally share it,
            use the regenerate button above to create a new URL.
          </p>
        </div>
      </div>

      {/* Regenerate Confirmation */}
      <ConfirmDialog
        isOpen={showRegenerateConfirm}
        title="Regenerate Feed URL"
        message="This will invalidate your current feed URL. Any calendars using the old URL will stop updating. Are you sure?"
        confirmLabel="Regenerate"
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={handleRegenerateConfirm}
        onCancel={() => setShowRegenerateConfirm(false)}
      />
    </div>
  );
}

